import { useState, useMemo, useEffect, useCallback } from 'react'
import { DateTime } from 'luxon'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons'
import { faMoneyBillTrendUp, faSparkle } from '@fortawesome/pro-solid-svg-icons'
import { selectInsightSummary } from '@/reducers/insights.slice'
import { getIsFetching } from '@/reducers/fetch'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  updateFinancialProfile,
  UPDATE_FINANCIAL_PROFILE_KEY,
} from '@/actions/financialProfileActions'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector, useToggle } from '@/utils/sharedHooks'
import {
  dollarsToCents,
  centsToDollars,
  formatCurrencyFromCentsAndRound,
} from '@/utils/currencyHelpers'

import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import {
  Button,
  Card,
  Progress,
  Input,
  Text,
  Icon,
  Badge,
  CardColors,
} from '@/ui'
import { selectIsCatchUpBookkeepingComplete } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { TextColors, TypographyStyles } from '@/styles'
import {
  INSIGHTS_TYPE,
  getInsightsMonthYear,
} from '@/features/financial-insights/shared/utils'
import {
  FETCH_INSIGHTS_SUMMARY_KEY,
  fetchInsightsSummary,
} from '@/features/financial-insights/shared/financeActions'

// goes in the new dashboard, has slightly different design than original
export const IncomeGoalCardV2 = () => {
  const dispatch = useAppDispatch()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const financialProfile = useReselector(getFinancialProfile)
  const isCUBKComplete = useReselector(selectIsCatchUpBookkeepingComplete)
  const updateLoading = useReselector(
    getIsFetching,
    UPDATE_FINANCIAL_PROFILE_KEY(financialProfile?.id)
  )

  const [annualIncome, setAnnualIncome] = useState(
    financialProfile?.annualIncomeGoalInCents
      ? centsToDollars(financialProfile.annualIncomeGoalInCents).toString()
      : ''
  )
  const [editing, toggleEditing] = useToggle()

  const { month, year } = getInsightsMonthYear(DateTime.now())

  useEffect(() => {
    pageView('income goal progress card')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchInsightsSummary(INSIGHTS_TYPE.INCOME, month, year))
  }, [dispatch, month, year])

  const summaryDataResponse = useReselector(
    selectInsightSummary,
    FETCH_INSIGHTS_SUMMARY_KEY(INSIGHTS_TYPE.INCOME, month, year)
  )

  const handleTrack = useCallback(async () => {
    await updateFinancialProfile(financialProfile?.id, {
      annualIncomeGoalInCents: dollarsToCents(annualIncome),
    })(dispatch)
    if (editing) {
      toggleEditing()
    }
  }, [annualIncome, dispatch, editing, financialProfile?.id, toggleEditing])

  const displayState = useMemo(() => {
    if (!isCUBKComplete) {
      return 'comingSoon'
    } else if (financialProfile?.annualIncomeGoalInCents === null) {
      return 'empty'
    } else if (editing) {
      return 'editing'
    } else {
      return 'progress'
    }
  }, [financialProfile, editing, isCUBKComplete])

  const incomeYearToDate = useMemo(() => {
    if (!summaryDataResponse?.[0]) return 0
    if (summaryDataResponse[0].ytd_total_in_cents === null) return 0
    return summaryDataResponse[0].ytd_total_in_cents
  }, [summaryDataResponse])

  const incomeGoalProgress = useMemo(() => {
    if (!financialProfile?.annualIncomeGoalInCents) return 0
    return Math.round(
      (incomeYearToDate / financialProfile.annualIncomeGoalInCents) * 100
    )
  }, [incomeYearToDate, financialProfile])

  const content = useMemo(() => {
    switch (displayState) {
      case 'comingSoon':
        return (
          <div className="flex items-center gap-6">
            <div>
              <Icon
                icon={faMoneyBillTrendUp}
                size="lg"
                color="text-neutral-0"
                backgroundColor="black"
              />
            </div>
            <div>
              <Text variant={TypographyStyles.H3} className="whitespace-nowrap">
                Annual Income Goal
              </Text>
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.MUTED}
                className="mt-2"
              >
                <span className="font-medium">Coming soon!</span> You&apos;ll be
                able to set your Annual Income Goal after your catch-up
                bookkeeping is complete.{' '}
                <button
                  onClick={() => {
                    document
                      .getElementsByClassName('inner-content-wrapper')[0]
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }}
                  className="text-charcoal underline cursor-pointer bg-transparent border-none p-0 font-inherit outline-none"
                >
                  What do I need to do next?
                </button>
              </Text>
            </div>
          </div>
        )
      case 'empty':
        return (
          <div className="space-y-6">
            <div className="flex flex-row items-center gap-2">
              <Badge color="orange" variant="secondary">
                <Icon
                  icon={faSparkle}
                  className="mr-2"
                  color="text-orange-600"
                />
                NEW
              </Badge>
              <Text variant={TypographyStyles.H2}>
                Track your annual income goals
              </Text>
            </div>

            <Text variant={TypographyStyles.BODY_SM} className="w-full">
              Enter your target annual income for {year} to track your progress
              here.
            </Text>
            <Input
              placeholder="$ Enter target income amount"
              value={annualIncome}
              onChange={(e) => setAnnualIncome(e.target.value)}
              className="w-1/3 bg-neutral-0"
              // TODO: Add currency input
              // componentType="currency"
            />

            <Button
              variant="secondary"
              className="w-1/3"
              onClick={() => {
                handleTrack()
                track('clicked track button', {
                  button: 'track income',
                })
              }}
              loading={updateLoading}
            >
              Track <Icon icon={faArrowRight} className="ml-2" />
            </Button>
          </div>
        )
      case 'progress':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Text variant={TypographyStyles.H2}>Annual Income Goal</Text>
              <Button
                onClick={() => {
                  toggleEditing()
                  track('clicked edit button', {
                    button: 'edit income',
                  })
                }}
                variant="link"
              >
                Edit
              </Button>
            </div>

            {/* Progress Display */}
            <div>
              <Text
                variant={TypographyStyles.DISPLAY_MD}
                color={TextColors.GREEN}
              >
                {incomeGoalProgress}%
              </Text>
              <Text variant={TypographyStyles.BODY_MD} color={TextColors.MUTED}>
                towards your {year} target annual income
              </Text>
            </div>

            {/* Progress Bar + Amounts */}
            <div className="space-y-2">
              <Progress value={incomeGoalProgress} />
              <Text
                variant={TypographyStyles.DISPLAY_XXS}
                color={TextColors.GREEN}
              >
                {formatCurrencyFromCentsAndRound(incomeYearToDate, {
                  hideDecimalsIfZero: true,
                })}{' '}
                /{' '}
                {formatCurrencyFromCentsAndRound(
                  financialProfile?.annualIncomeGoalInCents,
                  { hideDecimalsIfZero: true }
                )}
              </Text>
            </div>
          </div>
        )
      case 'editing':
        return (
          <div className="space-y-6">
            <Text variant={TypographyStyles.H2} className="w-full">
              Annual Income Goal
            </Text>

            <div>
              <Text variant={TypographyStyles.H3}>
                {year} Target Annual Income
              </Text>
              <Text variant={TypographyStyles.BODY_SM} className="mt-2 mb-2">
                Enter a new annual income goal. We&apos;ll update your progress
                to reflect the new target.
              </Text>
              <Input
                placeholder="$ Enter target income amount"
                value={annualIncome}
                onChange={(e) => setAnnualIncome(e.target.value)}
                className="w-1/3 bg-neutral-0"
                // TODO: Add currency input
                // componentType="currency"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <Button
                onClick={() => {
                  handleTrack()
                  track('clicked update goal button', {
                    button: 'update goal',
                  })
                }}
                loading={updateLoading}
              >
                Update Goal
              </Button>

              <Button
                onClick={toggleEditing}
                loading={updateLoading}
                variant="link"
              >
                Cancel
              </Button>
            </div>
          </div>
        )
      default:
        return displayState satisfies never
    }
  }, [
    annualIncome,
    displayState,
    financialProfile?.annualIncomeGoalInCents,
    handleTrack,
    incomeGoalProgress,
    incomeYearToDate,
    toggleEditing,
    track,
    updateLoading,
    year,
  ])

  return (
    <Card
      color={
        displayState === 'progress' || displayState === 'comingSoon'
          ? CardColors.LEVEL_1
          : CardColors.PRIORITY
      }
    >
      {content}
    </Card>
  )
}
